import { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';

export default function EditModal({ item, onSave, onClose, isSaving = false }) {
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    subreddit: '',
    timestamp: ''
  });
  const [followups, setFollowups] = useState([]);

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        body: item.body || '',
        subreddit: item.subreddit || '',
        timestamp: item.timestamp || ''
      });
      setFollowups(item.followups?.map(f => ({ ...f })) || []);
    }
  }, [item]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      followups
    });
  };

  const updateFollowup = (index, field, value) => {
    const updated = [...followups];
    updated[index] = { ...updated[index], [field]: value };
    setFollowups(updated);
  };

  if (!item) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content m-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit Post: {item.id}
          </h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Body
            </label>
            <textarea
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              className="textarea"
              rows={6}
            />
          </div>

          {/* Subreddit and Timestamp */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subreddit
              </label>
              <input
                type="text"
                value={formData.subreddit}
                onChange={(e) => setFormData({ ...formData, subreddit: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timestamp
              </label>
              <input
                type="text"
                value={formData.timestamp}
                onChange={(e) => setFormData({ ...formData, timestamp: e.target.value })}
                className="input"
              />
            </div>
          </div>

          {/* Followups */}
          {followups.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments ({followups.length})
              </label>
              <div className="space-y-3">
                {followups.map((followup, idx) => (
                  <div key={followup.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {followup.id} - u/{followup.username}
                      </span>
                      <span className="text-xs text-gray-500">
                        {followup.timestamp}
                      </span>
                    </div>
                    <textarea
                      value={followup.comment_text}
                      onChange={(e) => updateFollowup(idx, 'comment_text', e.target.value)}
                      className="textarea text-sm"
                      rows={2}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-1 flex-shrink-0 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1 flex-shrink-0" />
              )}
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
